import { createContext, ReactNode, useContext, useEffect } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
// Adjust this import if your SelectUser type definition changes due to schema modification
import { User as SelectUser } from "@shared/schema";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/utils/supabase";


type AuthContextType = {
  user: SelectUser | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser | { requiresEmailConfirmation: true; email: string; message: string }, Error, RegisterData>;
};

type LoginData = {
  email: string;
  password: string;
};

const registerSchema = z.object({
  email: z.string().email(),
  displayName: z.string().min(3),
  password: z.string().min(6),
  confirmPassword: z.string(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type RegisterData = z.infer<typeof registerSchema>;

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log('AuthProvider: Setting up onAuthStateChange listener.');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('AuthProvider: onAuthStateChange event:', event, 'Session:', session ? `User ID: ${session.user?.id}` : 'Null');
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          if (session?.user) {
            console.log('AuthProvider: User signed in/updated. Fetching profile from public.users for id (UUID):', session.user.id);
            try {
              // Profile is now fetched using the UUID id which is PK and FK to auth.users.id
              const { data, error } = await supabase
                .from('users')
                .select('*')
                .eq('id', session.user.id) // Querying by the new UUID PK
                .single();

              if (error) {
                console.error('AuthProvider: Error fetching user profile on auth change:', error);
                if (error.code === 'PGRST116') {
                  console.warn('AuthProvider: Profile not found in public.users for id:', session.user.id, "(This might be okay if email confirmation is pending and trigger hasn't made profile yet, or if trigger failed)");
                }
                queryClient.setQueryData(["/api/user"], null);
                // Reset Sentry user context when profile not found
                
                
              } else if (data) {
                console.log('AuthProvider: Profile fetched successfully on auth change:', data);
                queryClient.setQueryData(["/api/user"], data as SelectUser);
                // Set user context in Sentry for error tracking
                console.log('User authenticated:', {
                  id: session.user.id,
                  email: session.user.email
                });
                // Optional: add user role context
                if (data.role) {
                  
                }
              } else {
                 console.warn('AuthProvider: No profile data and no error for id:', session.user.id);
                 queryClient.setQueryData(["/api/user"], null);
                 // Reset Sentry user context
                 
              }
            } catch (catchError) {
              console.error('AuthProvider: Exception during profile fetch on auth state change:', catchError);
              queryClient.setQueryData(["/api/user"], null);
              // Capture the error in Sentry
              console.error("Authentication error during profile fetch:", catchError);
              // Reset Sentry user context
              
            }
          } else {
             queryClient.setQueryData(["/api/user"], null);
             // Reset Sentry user context
             
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('AuthProvider: User signed out.');
          queryClient.setQueryData(["/api/user"], null);
          // Reset Sentry user context on sign out
          
        }
      }
    );
    
    // Initial session check
    const checkInitialSession = async () => {
        console.log('AuthProvider: Initial session check.');
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
            console.log('AuthProvider: Active session on init. Fetching profile for id:', session.user.id);
             try {
              const { data: profileData, error: profileError } = await supabase
                .from('users')
                .select('*')
                .eq('id', session.user.id) // Query by new UUID PK
                .single();
              if (profileError) {
                console.error('AuthProvider: Error fetching profile on init:', profileError);
                 queryClient.setQueryData(["/api/user"], null);
                 // Reset Sentry user context
                 
              } else {
                 queryClient.setQueryData(["/api/user"], profileData as SelectUser);
                 // Set user context in Sentry
                 console.log('User authenticated on initial session check:', {
                   id: session.user.id,
                   email: session.user.email
                 });
              }
            } catch(e) {
                console.error('AuthProvider: Exception fetching profile on init', e);
                queryClient.setQueryData(["/api/user"], null);
                // Capture the error in Sentry
                console.error("Authentication error during initial session check:", e);
                // Reset Sentry user context
                
            }
        } else {
             queryClient.setQueryData(["/api/user"], null);
             // Reset Sentry user context
             
        }
    };
    checkInitialSession();

    return () => {
      console.log('AuthProvider: Unsubscribing from onAuthStateChange.');
      subscription.unsubscribe();
    };
  }, [queryClient]);

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<SelectUser | null>({
    queryKey: ["/api/user"],
    queryFn: async () => {
      console.log('useQuery /api/user: Fetching current Supabase auth user.');
      const { data: { user: authUser } } = await supabase.auth.getUser();

      if (!authUser) {
        console.log('useQuery /api/user: No Supabase auth user found.');
        return null;
      }
      console.log('useQuery /api/user: Auth user found. Fetching profile from public.users for id:', authUser.id);
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id) // Query by new UUID PK
          .single();

        if (profileError) {
          console.error('useQuery /api/user: Error fetching profile from public.users:', profileError);
          // Capture error in Sentry
          console.error("Error fetching user profile:", profileError);
          return null;
        }
        console.log('useQuery /api/user: Profile fetched from public.users:', profileData);
        return profileData as SelectUser;
      } catch (catchError) {
        console.error('useQuery /api/user: Exception fetching profile:', catchError);
        // Capture error in Sentry
        console.error("Exception fetching user profile:", catchError);
        return null;
      }
    },
  });

  const loginMutation = useMutation<SelectUser, Error, LoginData>({
    mutationFn: async (credentials: LoginData) => {
      try {
        console.log('loginMutation: Starting login for email:', credentials.email);
        
        // Sign in with Supabase Auth using email and password
        console.log('loginMutation: Signing in with Supabase Auth.');
        const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });

        if (signInError) {
          console.error('loginMutation: Supabase signInWithPassword failed.', signInError);
          // Log authentication failures to Sentry (without including passwords)
          
          throw new Error(signInError.message || 'Invalid username or password.');
        }
        
        if (!authData || !authData.user) {
          console.error('loginMutation: Supabase signInWithPassword returned no user.');
          
          throw new Error('Login failed: No user returned from authentication.');
        }
        
        console.log('loginMutation: Supabase signInWithPassword successful for user ID:', authData.user.id);

        // Step 3: Fetch full user profile
        console.log('loginMutation: Step 3 - Fetching full user profile from public.users.');
        const { data: userProfile, error: profileError } = await supabase
          .from('users')
          .select('*')
          .eq('id', authData.user.id)
          .single();

        if (profileError) {
          console.error('loginMutation: Profile fetch from public.users failed after login.', profileError);
          // Capture profile fetch errors in Sentry
          console.error("Error fetching user profile after login:", profileError);
          await supabase.auth.signOut(); // Sign out if profile is missing
          throw new Error('Failed to retrieve user profile after login.');
        }
        
        if (!userProfile) {
          console.error('loginMutation: User profile not found in database after login.');
          
          await supabase.auth.signOut();
          throw new Error('User profile not found. Please contact support.');
        }

        console.log('loginMutation: Login completed successfully. Profile:', userProfile);
        // Set Sentry user context
        console.log('User logged in successfully:', {
          id: authData.user.id,
          email: authData.user.email
        });
        return userProfile as SelectUser;
      } catch (error) {
        console.error('loginMutation: Caught exception during login:', error);
        console.error("Login process error:", error);
        throw error instanceof Error ? error : new Error('An unexpected error occurred during login.');
      }
    },
    onSuccess: (loggedInUser: SelectUser) => {
      console.log('loginMutation: onSuccess. User:', loggedInUser);
      
      // Force immediate cache update
      queryClient.setQueryData(["/api/user"], loggedInUser);
      
      toast({
        title: "Login successful",
        description: `Welcome back, ${loggedInUser.username}!`,
      });
      // Log successful login to Sentry for analytics
      
    },
    onError: (error: Error) => {
      console.error('loginMutation: onError.', error);
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation<
    SelectUser | { requiresEmailConfirmation: true; email: string; message: string },
    Error,
    RegisterData
  >({
    mutationFn: async (credentials: RegisterData) => {
      console.log('registerMutation: Starting registration for email:', credentials.email, 'display name:', credentials.displayName);
      const { confirmPassword, ...userData } = credentials;

      let authSignupResult: { data: { user: any; session: any; }; error: any; } | null = null; // To store the raw result

      try {
        console.log('registerMutation: Calling supabase.auth.signUp.');
        // Capture the raw result without immediate destructuring
        const rawResult = await supabase.auth.signUp({
          email: userData.email,
          password: userData.password,
            options: {
              emailRedirectTo: window.location.origin,
              data: {
                username: userData.displayName,
                display_name: userData.displayName
              }
            }
        });
        authSignupResult = rawResult; // Store the raw result
        
        // Log the raw result immediately
        console.log('registerMutation: Raw supabase.auth.signUp response received:', JSON.stringify(rawResult, null, 2));

        // Now, safely destructure and check
        const authSignupData = rawResult.data;
        const authSignupError = rawResult.error;

        console.log('registerMutation: Destructured supabase.auth.signUp response:', { 
            user: authSignupData?.user ? 'Exists' : 'Null/Undefined', 
            session: authSignupData?.session ? 'Exists' : 'Null/Undefined', 
            error: authSignupError ? { message: authSignupError.message, status: (authSignupError as any).status, name: authSignupError.name } : null 
        });

        if (authSignupError) {
          console.error('registerMutation: supabase.auth.signUp explicitly returned an error.', authSignupError);
          // Log signup errors to Sentry
          console.error("Signup error:", authSignupError);
          throw new Error(`Auth error: ${authSignupError.message}`);
        }
        
        // Handle various scenarios based on Supabase response structure
        // Scenario 1: Email confirmation is required, user object might be present, session is null
        if (authSignupData && authSignupData.user && authSignupData.session === null) {
            console.log('registerMutation: Email confirmation required (user exists, session is null). Trigger should have run.');
            
            return {
                requiresEmailConfirmation: true,
                email: userData.email,
                message: 'Please check your email to confirm your account. Your profile has been initiated.'
            };
        }
        
        // Scenario 2: Email confirmation required, user object might be null (e.g. if "Secure email change" is on for project)
        // Supabase docs state: "If you have enabled email confirmations, user will be null, and data.session will be null."
        // And also: "user: The user object. Null if email confirmation is required."
        if (authSignupData && authSignupData.user === null && authSignupData.session === null) {
             console.log('registerMutation: Email confirmation required (user and session are null post-signup). Trigger might have run if auth.users record was made.');
             console.log(`User registration pending email confirmation (null user): ${userData.email}`);
             return {
                requiresEmailConfirmation: true,
                email: userData.email,
                message: 'Please check your email to confirm your account.'
             };
        }

        // Scenario 3: Sign up successful and session created (email confirmation might be off or auto-confirmed)
        if (authSignupData && authSignupData.user && authSignupData.session) {
            console.log('registerMutation: Signup successful, user and session exist. auth.uid:', authSignupData.user.id);
            // Profile should have been created by trigger. Proceed to fetch it.
            
        } else if (!authSignupData || !authSignupData.user) { // Catch-all for unexpected "no user" without error
            console.error('registerMutation: supabase.auth.signUp returned no user and not a clear email confirmation case. Raw response:', JSON.stringify(rawResult, null, 2));
            console.error("Unexpected signup response", new Error('Registration failed: Unexpected response from auth signup.'));
            throw new Error('Registration failed: Unexpected response from auth signup.');
        }
        
        // Fetch profile (this assumes authSignupData.user.id is available)
        const userIdForProfileFetch = authSignupData.user!.id; // Non-null assertion as we expect user object if no error and not email-conf case above
        console.log('registerMutation: Auth user created/identified. Attempting to fetch profile created by trigger for id:', userIdForProfileFetch);

        let profile: SelectUser | null = null;
        let attempts = 0;
        while (!profile && attempts < 5) {
            attempts++;
            await new Promise(resolve => setTimeout(resolve, 500 * attempts));
            console.log(`registerMutation: Profile fetch attempt ${attempts} for id: ${userIdForProfileFetch}`);
            const { data: fetchedProfile, error: fetchError } = await supabase
                .from('users')
                .select('*')
                .eq('id', userIdForProfileFetch)
                .single();
            
            if (fetchError && fetchError.code !== 'PGRST116') {
                 console.error(`registerMutation: Error fetching profile on attempt ${attempts}:`, fetchError);
                 // Log profile fetch errors to Sentry
                 if (attempts === 5) {
                   console.error("Error fetching profile after signup", fetchError);
                 }
            } else if (fetchedProfile) {
                profile = fetchedProfile as SelectUser;
                console.log(`registerMutation: Profile fetched successfully on attempt ${attempts}:`, profile);
                break;
            } else {
                console.warn(`registerMutation: Profile not found on attempt ${attempts} for id:`, userIdForProfileFetch);
                if (attempts === 5) {
                  
                }
            }
        }

        if (!profile) {
            console.error('registerMutation: Failed to fetch profile from public.users after multiple attempts.');
            console.error("Failed to retrieve user profile after signup", {
              action: "fetchProfileAfterMultipleAttempts",
              userId: userIdForProfileFetch,
              attempts: attempts
            });
            throw new Error('Failed to retrieve user profile after signup. Please try again later or contact support.');
        }
        
        console.log('registerMutation: Registration and profile fetch successful. Profile:', profile);
        // Set Sentry user context
        console.log('User registered successfully:', {
          id: userIdForProfileFetch,
          email: userData.email
        });
        return profile;

      } catch (errorCatched) {
        // Log the raw authSignupResult if available, for more context on the error
        if (authSignupResult) {
            console.error('registerMutation: Raw authSignupResult at time of exception:', JSON.stringify(authSignupResult, null, 2));
        }
        console.error('registerMutation: Exception during registration process.', errorCatched);
        console.error("Registration exception", {
          action: "registrationException",
          error: errorCatched
        });
        if (errorCatched instanceof Error) {
            throw errorCatched;
        } else {
            throw new Error(String(errorCatched));
        }
      }
    },
    onSuccess: (result) => {
      console.log('registerMutation: onSuccess. Result:', result);
      if (result && 'requiresEmailConfirmation' in result && result.requiresEmailConfirmation) {
        toast({
          title: "Verification Email Sent",
          description: result.message,
        });
      } else if (result) {
        // This means SelectUser was returned
        // queryClient.setQueryData(["/api/user"], result as SelectUser); // onAuthStateChange will handle this
        toast({
          title: "Registration successful!",
          description: `Welcome, ${(result as SelectUser).displayName || (result as SelectUser).username}! You are now logged in.`,
        });
        // Log successful registration to Sentry for analytics
        console.log(`User registration completed: ${(result as SelectUser).username}`);
      } else {
        // Should not happen if mutationFn throws errors correctly
        console.error("registerMutation onSuccess: Received undefined result without email confirmation flag.");
        
      }
    },
    onError: (error: Error) => {
      console.error('registerMutation: onError.', error);
      let errorMessage = error.message;
       if (error.message.toLowerCase().includes('user already registered')) {
        errorMessage = 'This email or username is already registered. Please try logging in or use different credentials.';
      } else if (error.message.toLowerCase().includes('password should be at least 6 characters') || error.message.toLowerCase().includes('weak password')) {
        errorMessage = 'Password is too weak. Please use a stronger password.';
      }
      toast({
        title: "Registration Failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      console.log('logoutMutation: Signing out.');
      // Capture logout event in Sentry
      if (user) {
        
      }
      const { error: signOutError } = await supabase.auth.signOut();
      if (signOutError) {
        console.error('logoutMutation: Supabase signOut failed.', signOutError);
        console.error("Signout error", {
          action: "signOut",
          error: signOutError
        });
        throw new Error(signOutError.message);
      }
      console.log('logoutMutation: Sign out successful.');
      // Clear Sentry user context
      
    },
    onSuccess: () => {
      console.log('logoutMutation: onSuccess.');
      queryClient.clear(); // Clear all react-query cache
      toast({
        title: "Logged Out",
        description: "You have been successfully logged out.",
      });
    },
    onError: (error: Error) => {
      console.error('logoutMutation: onError.', error);
      toast({
        title: "Logout Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
